import type { RouteRecordRaw } from 'vue-router'
import { ChatSettingsAliasedRoutes, ChatSettingsRoutes } from '@/routes/types'
import { ByocMessengerRouteNames, ByocMessengerRoutePaths } from '@/services/domain/byoc/types'
import {
  getOldFacebookRoutes,
  getOldInstagramRoutes,
  getOldWhatsAppRoutes,
  getOldWidgetRoutes,
  oldMessengerSettingsEmailsRoute,
} from '@/routes/user/messenger/oldMessengerRoutes'
import { getOldRouteWithRedirect } from '@/utils/oldRouteRedirect'
import { UserSettingsRoutes } from '@/routes/user/settings'

export const getMessengerSettingsRoutes = (): RouteRecordRaw[] => [
  {
    name: ChatSettingsRoutes.root,
    path: 'settings',
    redirect: { name: 'user.chats.settings.all' },
    children: [
      getOldRouteWithRedirect('user.chats.settings.all', 'all', UserSettingsRoutes.SmsVoiceIndex),
      {
        name: 'user.messenger.settings.smsMmsCalls',
        path: 'sms-mms-calls',
        children: [
          getOldRouteWithRedirect(ChatSettingsRoutes.smsGeneral, 'general', UserSettingsRoutes.SmsVoiceIndex),
          {
            name: ChatSettingsRoutes.smsMmsCalls,
            path: '',
            redirect: { name: ChatSettingsRoutes.smsGeneral },
            children: [
              {
                name: ChatSettingsRoutes.smsMmsCallsBuyNumberBase,
                path: 'buy-number',
                alias: ChatSettingsAliasedRoutes.buyNumber,
                redirect: { name: ChatSettingsRoutes.smsMmsCallsBuyNumber },
                children: [
                  getOldRouteWithRedirect(
                    ChatSettingsRoutes.smsMmsCallsBuyNumber,
                    'number',
                    UserSettingsRoutes.SmsVoiceBuyNumber,
                  ),
                  getOldRouteWithRedirect(
                    ChatSettingsRoutes.smsMmsCallsCallForwarding,
                    'call-forwarding/:id',
                    UserSettingsRoutes.SmsVoiceCallForwarding,
                  ),
                ],
              },
              {
                name: 'user.messenger.settings.smsMmsCalls.calls',
                path: '',
                children: [
                  getOldRouteWithRedirect(
                    ChatSettingsRoutes.smsMmsCallsNumbers,
                    'numbers',
                    UserSettingsRoutes.SmsVoiceNumbers,
                  ),
                  getOldRouteWithRedirect(
                    ChatSettingsRoutes.smsMmsCallsSenderIds,
                    'sender-ids',
                    UserSettingsRoutes.SmsVoiceSenderIds,
                  ),
                ],
              },
            ],
          },
          {
            name: ByocMessengerRouteNames.main,
            path: 'byoc',
            redirect: { name: ByocMessengerRouteNames.providers },
            children: [
              {
                name: ByocMessengerRouteNames.byocProvidersBase,
                path: '',
                redirect: { name: ByocMessengerRouteNames.providers },
                children: [
                  getOldRouteWithRedirect(
                    ByocMessengerRouteNames.providers,
                    'providers',
                    UserSettingsRoutes.SmsVoiceByocProviders,
                  ),
                  getOldRouteWithRedirect(
                    ByocMessengerRouteNames.phones,
                    'numbers',
                    UserSettingsRoutes.SmsVoiceByocPhones,
                  ),
                  getOldRouteWithRedirect(
                    ByocMessengerRouteNames.senderIds,
                    'sender-ids',
                    UserSettingsRoutes.SmsVoiceByocSenderIds,
                  ),
                ],
              },
              {
                name: ByocMessengerRouteNames.edit,
                path: ':provider/:carrierId',
                alias: ByocMessengerRoutePaths.edit,
                redirect: { name: UserSettingsRoutes.SmsVoiceByocEdit },
              },
              {
                name: ByocMessengerRouteNames.callForwarding,
                path: ':provider/:carrierId/:phoneNumberId/call-forwarding',
                alias: ByocMessengerRoutePaths.callForwarding,
                redirect: { name: UserSettingsRoutes.SmsVoiceByocCallForwarding },
              },
              getOldRouteWithRedirect(ByocMessengerRouteNames.flow, 'flow', UserSettingsRoutes.SmsVoiceByocFlow),
            ],
          },
          getOldRouteWithRedirect(
            ChatSettingsRoutes.senderSettings,
            'sender-settings',
            UserSettingsRoutes.SmsVoiceSenderSettings,
          ),
          getOldRouteWithRedirect(ChatSettingsRoutes.tenDlc, '10dlc', UserSettingsRoutes.SmsVoiceTenDlc),
          getOldRouteWithRedirect(
            ChatSettingsRoutes.tollFree,
            'toll-free-verification',
            UserSettingsRoutes.SmsVoiceTollFree,
          ),
        ],
      },
      {
        // This block redirect old byoc routes to the new ones
        name: 'byoc-fallback',
        path: 'byoc',
        redirect: { name: ByocMessengerRouteNames.providers },
        children: [
          {
            name: `${ByocMessengerRouteNames.byocProvidersBase}.fallback`,
            path: '',
            redirect: { name: ByocMessengerRouteNames.byocProvidersBase },
            children: [
              {
                name: `${ByocMessengerRouteNames.providers}.fallback`,
                path: 'providers',
                redirect: { name: ByocMessengerRouteNames.providers },
              },
              {
                name: `${ByocMessengerRouteNames.phones}.fallback`,
                path: 'numbers',
                redirect: { name: ByocMessengerRouteNames.phones },
              },
              {
                name: `${ByocMessengerRouteNames.senderIds}.fallback`,
                path: 'sender-ids',
                redirect: { name: ByocMessengerRouteNames.senderIds },
              },
              {
                name: `${ByocMessengerRouteNames.edit}.fallback`,
                path: ':provider/:carrierId',
                redirect: (to) => {
                  return {
                    name: ByocMessengerRouteNames.edit,
                    params: to.params,
                    query: to.query,
                  }
                },
              },
              {
                name: `${ByocMessengerRouteNames.callForwarding}.fallback`,
                path: ':provider/:carrierId/:phoneNumberId/call-forwarding',
                redirect: (to) => {
                  return {
                    name: ByocMessengerRouteNames.callForwarding,
                    params: to.params,
                    query: to.query,
                  }
                },
              },
              {
                name: `${ByocMessengerRouteNames.flow}.fallback`,
                path: 'flow',
                redirect: { name: ByocMessengerRouteNames.flow },
              },
            ],
          },
        ],
      },
      oldMessengerSettingsEmailsRoute as RouteRecordRaw,
      ...getOldWhatsAppRoutes(),
      ...getOldFacebookRoutes(),
      ...getOldInstagramRoutes(),
      ...getOldWidgetRoutes(),
      getOldRouteWithRedirect('user.chats.settings.general', 'general', UserSettingsRoutes.Messenger),
      {
        // This block redirects old route to the new one
        name: `${ChatSettingsRoutes.tenDlc}.fallback`,
        path: '10dlc',
        redirect: { name: ChatSettingsRoutes.tenDlc },
      },
      {
        // This block redirects old route to the new one
        name: `${ChatSettingsRoutes.tollFree}.fallback`,
        path: 'toll-free-verification',
        redirect: { name: ChatSettingsRoutes.tollFree },
      },
      {
        // This block redirects old route to the new one
        name: `${ChatSettingsRoutes.senderSettings}.fallback`,
        path: 'sender-settings',
        redirect: { name: UserSettingsRoutes.SmsVoiceSenderSettings },
      },
    ],
  },
]
